'use strict';
import {gsap, ScrollTrigger} from '../../../src/js/shared/gsap';

const $ = jQuery.noConflict();

$(function() {
	const pageAnimation = {

		init: function() {
			if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

			this.cacheDOM();
			if (!this.$wrapper.length || !this.$svgBox.length) return;

			this.readSettings();
			this.anchorToPageTop();
			this.capSvgHeight();
			this.buildTimeline();
			if (!this.tl) return;

			// re-measure the SVG's position/height any time ScrollTrigger recalculates
			// (window resize, or the explicit refresh on window load below) instead of
			// wiring up a separate resize listener
			ScrollTrigger.addEventListener('refreshInit', () => {
				this.anchorToPageTop();
				this.capSvgHeight();
			});

			// images (this theme lazy-loads them) and webfonts can still shift the
			// footer/hero down after the initial measurement, so resync once
			// everything has actually finished loading
			$(window).on('load.pageAnimation', () => ScrollTrigger.refresh());

			this.start();
		},

		cacheDOM: function() {
			this.$wrapper = $('.js-page-animation');
			this.$svgBox = this.$wrapper.find('.page-animation__svg');
		},

		// editor-configurable via the "Intro Progress %" ACF field — how far the line
		// draws on page load before scroll takes over. "Disable Scroll Animation"
		// bypasses ScrollTrigger entirely and always draws to 100% on load instead.
		readSettings: function() {
			const rawProgress = parseFloat(this.$wrapper.data('introProgress'));
			this.introFloor = this.clamp(isNaN(rawProgress) ? 0.15 : rawProgress / 100, 0, 1);
			this.scrollDisabled = String(this.$wrapper.data('disableScrollAnimation')) === '1';
		},

		// the block can end up anywhere in the page's flexible-content stack, but the
		// drawing itself is authored to start at the top of the page (the hero) — so
		// visually pull the whole graphic up (or down) to line up with the hero,
		// regardless of where in the DOM this block actually sits
		anchorToPageTop: function() {
			const hero = document.querySelector('.banner');
			const wrapper = this.$wrapper.get(0);

			if (!hero) return;

			const scrollY = window.pageYOffset || document.documentElement.scrollTop;
			const heroTop = hero.getBoundingClientRect().top + scrollY;
			const wrapperTop = wrapper.getBoundingClientRect().top + scrollY;

			wrapper.style.transform = 'translateY(' + (heroTop - wrapperTop) + 'px)';
		},

		// the source drawing's height doesn't necessarily match how tall the page
		// actually is, so cap the SVG box at the footer's top edge — otherwise the
		// absolutely positioned artwork paints past the last block and stretches the
		// page's scrollable height with empty space. the inner <svg> is then stretched
		// (non-uniformly, if needed) to exactly fill that height rather than keeping
		// its native aspect ratio, so the drawing always finishes at the footer
		// regardless of how the source SVG's own proportions compare to the page
		capSvgHeight: function() {
			const footer = document.getElementById('colophon');
			const box = this.$svgBox.get(0);
			const svg = box ? box.querySelector('svg') : null;

			if (!footer || !box || !svg) return;

			box.style.maxHeight = '';
			svg.style.height = '';

			const scrollY = window.pageYOffset || document.documentElement.scrollTop;
			const footerTop = footer.getBoundingClientRect().top + scrollY;
			const boxTop = box.getBoundingClientRect().top + scrollY;
			const availableHeight = Math.max(0, footerTop - boxTop);

			box.style.maxHeight = availableHeight + 'px';
			svg.setAttribute('preserveAspectRatio', 'none');
			svg.style.height = availableHeight + 'px';
		},

		// builds one timeline out of every <path> in the SVG, whatever their number or
		// length, and chains them back to back so the whole thing draws as a single
		// continuous line: each tween's duration is its own path length, so the
		// timeline's total duration is the SVG's total arc length — that lets the rest
		// of the code treat "draw to X" as "set tl.time(X)" without ever hand-rolling
		// per-path stroke-dasharray/dashoffset math itself (DrawSVGPlugin owns that)
		buildTimeline: function() {
			const pathEls = this.$wrapper.get(0).querySelectorAll('svg path');
			if (!pathEls.length) return;

			this.tl = gsap.timeline({paused: true});
			this.buildDepthMap(pathEls);
		},

		// arc length isn't spread evenly over the drawing's own vertical extent — a
		// tight decorative loop packs a lot of length into a small vertical span, a
		// fast diagonal stretch covers a lot of vertical span in very little length.
		// mapping scroll straight to arc length lets the drawn tip drift far ahead of
		// (or behind) the visitor's actual scroll depth on those stretches. so we
		// sample every path's vertical (y) position along its length once at init and
		// build a *smoothed* length<->depth curve — a wide moving average, not a hard
		// non-decreasing cap — so the reveal rate eases down on fast stretches and
		// eases up on dense ones without ever visibly freezing: a hard cap tied to
		// raw local peaks stalls completely until scroll catches up to that exact
		// peak, which reads as the animation getting stuck.
		buildDepthMap: function(pathEls) {
			const raw = [];
			let virtualLength = 0;

			pathEls.forEach((path) => {
				const length = path.getTotalLength();
				this.tl.fromTo(path, {drawSVG: '0%'}, {drawSVG: '100%', duration: length, ease: 'none'});

				const steps = Math.max(8, Math.min(400, Math.ceil(length / 12)));
				for (let i = 0; i <= steps; i++) {
					const distance = (length * i) / steps;
					raw.push({length: virtualLength + distance, y: path.getPointAtLength(distance).y});
				}

				virtualLength += length;
			});

			if (!raw.length) return;

			// a window spanning roughly a tenth of the drawing's length blurs out
			// local loops/reversals while preserving the overall top-to-bottom trend
			const windowSize = Math.max(1, Math.round(raw.length * 0.06));
			let runningMaxY = -Infinity;
			let minY = Infinity;

			this.depthMap = raw.map((sample, i) => {
				let sum = 0;
				let count = 0;
				for (let j = Math.max(0, i - windowSize); j <= Math.min(raw.length - 1, i + windowSize); j++) {
					sum += raw[j].y;
					count++;
				}
				const smoothedY = sum / count;
				runningMaxY = Math.max(runningMaxY, smoothedY);
				minY = Math.min(minY, smoothedY);
				return {length: sample.length, y: runningMaxY};
			});

			this.minY = minY;
			this.maxY = runningMaxY;
		},

		// given a 0-1 fraction of the drawing's own vertical extent, returns the arc
		// length the smoothed depth curve reaches at that depth — samples are sorted
		// by length ascending and the smoothed y is non-decreasing, so this is a
		// standard binary search for the last sample within bounds
		lengthForDepth: function(fraction) {
			if (!this.depthMap || !this.depthMap.length) return 0;

			const targetY = this.minY + this.clamp(fraction, 0, 1) * (this.maxY - this.minY);
			let lo = 0;
			let hi = this.depthMap.length - 1;
			let result = 0;

			while (lo <= hi) {
				const mid = Math.floor((lo + hi) / 2);
				if (this.depthMap[mid].y <= targetY) {
					result = mid;
					lo = mid + 1;
				} else {
					hi = mid - 1;
				}
			}

			return this.depthMap[result].length;
		},

		// scales the intro's real-world duration off the SVG's total arc length so a
		// short line and a very long one both feel like the same speed of drawing
		// rather than the long one finishing suspiciously fast or the short one crawling
		introDuration: function() {
			return gsap.utils.clamp(1.1, 3.2, this.tl.duration() / 3500);
		},

		start: function() {
			if (this.scrollDisabled) {
				this.playIntro(this.tl.duration());
				return;
			}

			const introTime = this.lengthForDepth(this.introFloor);

			if (this.isAtPageTop()) {
				this.playIntro(introTime, () => this.initScrollTrigger(introTime));
			} else {
				// mid-page load (refresh scrolled down, anchor link, etc.) — skip the
				// intro and let ScrollTrigger sync the drawing to the current scroll
				// position immediately, with no animation
				this.initScrollTrigger(introTime);
			}
		},

		isAtPageTop: function() {
			return (window.pageYOffset || document.documentElement.scrollTop) <= 2;
		},

		playIntro: function(targetTime, onHandoff) {
			const handoff = () => {
				$(window).off('.pageAnimationIntro');
				if (onHandoff) onHandoff();
			};

			const introTween = gsap.to(this.tl, {
				time: targetTime,
				duration: this.introDuration(),
				ease: 'power2.out',
				onComplete: handoff,
			});

			if (!onHandoff) return;

			// if the visitor starts scrolling before the intro finishes, hand off to
			// ScrollTrigger right away instead of fighting the still-running tween
			$(window).one('scroll.pageAnimationIntro wheel.pageAnimationIntro touchmove.pageAnimationIntro', () => {
				introTween.kill();
				handoff();
			});
		},

		// scroll maps to a depth fraction (from the intro's own floor up to fully
		// drawn), looked up through the smoothed depth map, rather than straight to
		// the timeline's raw progress — so scrollY = 0 always maps back to exactly
		// where the intro left off (seamless handoff, and scrolling back to the top
		// settles on the intro floor, never blank), and fast/sparse stretches of the
		// drawing ease rather than race ahead of the viewport.
		//
		// the target length still jumps around frame to frame (the drawing's local
		// speed genuinely varies along its length), so this only tracks *where the
		// pen should end up*, not what gets drawn on screen — see renderScroll.
		initScrollTrigger: function(introTime) {
			this.targetLength = introTime;
			this.displayedLength = this.tl.time();

			ScrollTrigger.create({
				start: 0,
				end: 'max',
				scrub: true,
				onUpdate: (self) => {
					const depth = this.introFloor + (1 - this.introFloor) * self.progress;
					this.targetLength = Math.max(introTime, this.lengthForDepth(depth));
				},
			});

			gsap.ticker.add(this.renderScroll.bind(this));
		},

		// eases the actually-drawn length toward the scroll-derived target every
		// frame instead of snapping straight to it, so the reveal always reads as
		// one continuous, steady pen stroke rather than jumping in bursts whenever
		// the drawing's local speed changes — stops touching the DOM once caught up
		// so it doesn't keep re-rendering at rest
		renderScroll: function() {
			const delta = this.targetLength - this.displayedLength;
			if (Math.abs(delta) < 0.5) return;

			this.displayedLength += delta * 0.15;
			this.tl.time(this.displayedLength);
		},

		clamp: function(value, min, max) {
			return Math.min(Math.max(value, min), max);
		},
	};

	pageAnimation.init();
});
